import React, { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PickerDate } from "@/components/PickerDate";
import { RcAppsPicker } from "@/components/rcs/rc-apps-picker";
import RcTasksSection from "@/components/rcs/rc-tasks-section";
import { clientApps, useApps } from "@/hooks/apps";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { mutateRequest, patchListItem, postFetcher, putFetcher, useAuthStore, writeAtom } from "@/lib/swr";
import {
  RC_CONCLUSIONS,
  RC_STATUSES,
  RC_TYPES,
  RcRow,
  RcTask,
  refreshRcTasks,
} from "@/constants/rc";

// Full reload of the RC list page: the list and the tab counts (stats). Flow
// actions (close / deploy / conclude) change server-derived stage, so they
// still go through here.
export const refreshRcs = () => {
  mutateRequest("/remote-configs");
  mutateRequest("/remote-configs/stats");
};

// Edit: the PUT echoes the RC — patch the list row and detail in place (the
// server-only `stage` stays as cached) and only the stats reload (WED-249).
// Create: the new row's position / tab depends on server sorting, so the list
// reloads once.
const applySaved = (saved: RcRow | undefined, editing: boolean) => {
  if (!editing || !saved?.id) return refreshRcs();
  patchListItem("/remote-configs", saved.id, { ...saved });
  writeAtom<RcRow>(`/remote-configs/${saved.id}`, (cur) => ({ ...cur, ...saved }));
  mutateRequest("/remote-configs/stats");
};

const csv = (s: string): string[] =>
  s
    .split(/[,，、\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

// Shared create/edit form — the in-app replacement of the Feishu "RC 配置申请"
// form view. The requester defaults to the current user server-side.
// 只收申请信息:节点交付物字段(Status/Conclusion/Firebase 链接/Deploy note)
// 在详情页对应流程节点由对应角色回填(服务端字段级校验同口径),这里仅
// admin 保留直改入口(修数/解卡)。
export const RcFormDialog = ({
  pid,
  rc,
  trigger,
  onSaved,
}: {
  /** Lock the app when opened from an app-scoped tab. */
  pid?: string;
  /** Present = edit mode. */
  rc?: RcRow;
  trigger: React.ReactNode;
  onSaved?: () => void;
}) => {
  const { toast } = useToast();
  const apps = useApps();
  const me = useAuthStore((s) => s.auth?.user);
  const [open, setOpen] = useState(false);
  const editing = !!rc;
  const isAdmin = !!me?.isAdmin;
  // 发起回滚是需求方动作(历史行没 requesterId 不拦,与服务端同口径)
  const isRequester =
    !rc?.requesterId || me?.id === rc?.requesterId || isAdmin;

  // 建单可多选 App(WED-245):服务端按每个 App 各建一条 RC。编辑 / App
  // 页内建单锁定为单个 App。
  const initialAppIds = () => {
    const one = rc?.appId ?? pid;
    return one ? [String(one)] : [];
  };
  const [appIds, setAppIds] = useState<string[]>(initialAppIds);
  // 选项缺省只给客户端 App(clientApps 按 isApp 过滤),但 RC 可能挂在后端
  // 项目上(如 Wednesday 自己):当前值不在选项里时渲染不出名字,看着像
  // "绑错了"。把已选但不在列表里的 App 补进来。
  const appOptions = (() => {
    const list = clientApps(apps);
    const extra = appIds
      .filter((id) => !list.some((a) => String(a.id) === id))
      .map((id) => apps.find((a) => String(a.id) === id))
      .filter((a): a is NonNullable<typeof a> => !!a);
    return extra.length ? [...list, ...extra] : list;
  })();
  const [name, setName] = useState(rc?.name ?? "");
  const [type, setType] = useState<string>(rc?.type ?? "AB_EXPERIMENT");
  const [status, setStatus] = useState<string>(rc?.status ?? "NOT_STARTED");
  const [countries, setCountries] = useState((rc?.countries ?? []).join(", "));
  const [modules, setModules] = useState((rc?.modules ?? []).join(", "));
  const [startDate, setStartDate] = useState<Date | null>(
    rc?.startDate ? new Date(rc.startDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    rc?.endDate ? new Date(rc.endDate) : null
  );
  const [docLink, setDocLink] = useState(rc?.docLink ?? "");
  const [configDoc, setConfigDoc] = useState(rc?.configDoc ?? "");
  const [firebaseLink, setFirebaseLink] = useState(rc?.rcFirebaseLink ?? "");
  const [relatedLink, setRelatedLink] = useState(rc?.relatedTaskOrPrdLink ?? "");
  const [conclusion, setConclusion] = useState(rc?.conclusion ?? "");
  const [remarks, setRemarks] = useState(rc?.remarks ?? "");
  const [deployNote, setDeployNote] = useState(rc?.deployNote ?? "");

  const reset = () => {
    setAppIds(initialAppIds());
    setName(rc?.name ?? "");
    setType(rc?.type ?? "AB_EXPERIMENT");
    setStatus(rc?.status ?? "NOT_STARTED");
    setCountries((rc?.countries ?? []).join(", "));
    setModules((rc?.modules ?? []).join(", "));
    setStartDate(rc?.startDate ? new Date(rc.startDate) : null);
    setEndDate(rc?.endDate ? new Date(rc.endDate) : null);
    setDocLink(rc?.docLink ?? "");
    setConfigDoc(rc?.configDoc ?? "");
    setFirebaseLink(rc?.rcFirebaseLink ?? "");
    setRelatedLink(rc?.relatedTaskOrPrdLink ?? "");
    setConclusion(rc?.conclusion ?? "");
    setRemarks(rc?.remarks ?? "");
    setDeployNote(rc?.deployNote ?? "");
  };

  const onError = (error: unknown) =>
    toastError(
      toast,
      editing ? "Failed to update RC" : "Failed to create RC",
      error
    );
  // 新建传 pids 时服务端回数组(每 App 一条),列表整体重载即可
  const onSuccess = (saved?: RcRow | RcRow[]) => {
    setOpen(false);
    applySaved(Array.isArray(saved) ? undefined : saved, editing);
    onSaved?.();
  };

  const { trigger: add, isMutating: adding } = useSWRMutation(
    "/remote-configs",
    postFetcher<RcRow[]>,
    { onSuccess, onError }
  );
  const { trigger: save, isMutating: saving } = useSWRMutation(
    `/remote-configs/${rc?.id}`,
    putFetcher<RcRow>,
    { onSuccess, onError }
  );
  // "Initiate close": files a close task for the RC operators to confirm —
  // the RC only turns CLOSED once an operator confirms that task.
  const { trigger: closeRc, isMutating: closing } = useSWRMutation(
    `/remote-configs/${rc?.id}/close`,
    postFetcher,
    {
      onSuccess: () => {
        toast({
          title: "Close task created",
          description: "Waiting for an operator to confirm.",
        });
        refreshRcTasks(rc?.id);
        onSuccess();
      },
      onError: (error: Error) =>
        toastError(toast, "Failed to initiate close", error),
    }
  );
  const isMutating = adding || saving || closing;

  // Flow tasks, fetched only while the edit dialog is open (same key as the
  // tasks section / row chips, so SWR dedupes). Drives the close button state.
  const { data: tasks } = useSWR<RcTask[]>(
    rc && open ? [`/remote-configs/${rc.id}/tasks`] : null
  );
  const pendingClose = !!tasks?.some(
    (t) => t.type === "close" && !t.confirmedAt
  );

  const submit = () => {
    if (!appIds.length || !name.trim() || isMutating) return;
    const payload: Record<string, unknown> = {
      // 编辑只认单个 App;新建传 pids,服务端按 App 各建一条(返回数组)
      ...(editing ? { pid: appIds[0] } : { pids: appIds }),
      name: name.trim(),
      type,
      countries: csv(countries),
      modules: csv(modules),
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      docLink: docLink.trim() || null,
      configDoc: configDoc.trim() || null,
      relatedTaskOrPrdLink: relatedLink.trim() || null,
      remarks: remarks.trim() || null,
    };
    // 节点交付物字段只有 admin 从这里直改;其他人不带 = 不改
    if (editing && isAdmin) {
      payload.status = status;
      payload.conclusion = conclusion || null;
      payload.rcFirebaseLink = firebaseLink.trim() || null;
      payload.deployNote = deployNote.trim() || null;
    }
    if (editing) save(payload);
    else add(payload);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) reset();
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit RC config" : "New RC config"}</DialogTitle>
          <DialogDescription>
            A Remote Config switch tracked per app and country — A/B test,
            deploy config or ops adjustment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">
                {editing || pid ? "App" : "Apps (one RC per app)"}
              </span>
              <RcAppsPicker
                value={appIds}
                onChange={setAppIds}
                options={appOptions}
                disabled={!!pid || editing}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">Type</span>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RC_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">Name</span>
            <Input
              value={name}
              placeholder="e.g. HD-KR native recolor test"
              autoFocus={!editing}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              className="h-8"
            />
          </label>
          {/* Status/Conclusion 是流程产物:状态由节点流转推进,结论在
              等待数据回收节点回填;仅 admin 保留直改入口(修数/解卡) */}
          {editing && isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="text-sm text-muted-foreground">
                  Status (admin)
                </span>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger size="sm" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RC_STATUSES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="block space-y-1">
                <span className="text-sm text-muted-foreground">
                  Conclusion (admin)
                </span>
                <Select
                  value={conclusion || "__none"}
                  onValueChange={(v) => setConclusion(v === "__none" ? "" : v)}
                >
                  <SelectTrigger size="sm" className="w-full">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {RC_CONCLUSIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">Countries</span>
              <Input
                value={countries}
                placeholder="BR, MX or ALL"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCountries(e.target.value)
                }
                className="h-8"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">Modules</span>
              <Input
                value={modules}
                placeholder="SL, Search, 商业化…"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setModules(e.target.value)
                }
                className="h-8"
              />
            </label>
          </div>
          {/* 申请不收操作人:节点该谁做由 App 的 RC flow 配置决定(配谁
              就是谁);历史行的 operators 仅在详情页展示 */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">Start date</span>
              <PickerDate date={startDate} setDate={setStartDate} />
            </label>
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">End date</span>
              <PickerDate date={endDate} setDate={setEndDate} />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">RC doc link</span>
            <Input
              value={docLink}
              placeholder="https://…feishu.cn/docx/…"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDocLink(e.target.value)
              }
              className="h-8 text-xs"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">
              Config doc (markdown) — usually written by the wed-rc skill
            </span>
            <Textarea
              value={configDoc}
              rows={8}
              placeholder="# App-Country-Topic … full config, rendered by wed-rc"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setConfigDoc(e.target.value)
              }
              className="font-mono text-xs"
            />
          </label>
          {/* Firebase 链接是待配置节点的交付物,由配置操作人在详情页节点
              回填;仅 admin 保留直改 */}
          {editing && isAdmin && (
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">
                Experiment link (Firebase console, admin)
              </span>
              <Input
                value={firebaseLink}
                placeholder="https://console.firebase.google.com/…"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFirebaseLink(e.target.value)
                }
                className="h-8 text-xs"
              />
            </label>
          )}
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">
              Related task / PRD link
            </span>
            <Input
              value={relatedLink}
              placeholder="https://…"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRelatedLink(e.target.value)
              }
              className="h-8 text-xs"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">Remarks</span>
            <Textarea
              value={remarks}
              rows={2}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setRemarks(e.target.value)
              }
            />
          </label>
          {/* 部署说明是部署申请的一部分(需求方在流程节点提交);仅 admin
              保留直改 */}
          {editing && isAdmin && (
            <label className="block space-y-1">
              <span className="text-sm text-muted-foreground">
                Deploy note (admin)
              </span>
              <Textarea
                value={deployNote}
                rows={2}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDeployNote(e.target.value)
                }
              />
            </label>
          )}
          {rc && <RcTasksSection rcId={rc.id} />}
        </div>
        <DialogFooter>
          {editing && rc?.status !== "CLOSED" && isRequester && (
            <Button
              variant="destructive"
              size="sm"
              disabled={isMutating || pendingClose}
              onClick={() => closeRc({})}
              title={
                pendingClose
                  ? "A close task is already waiting for operator confirmation"
                  : "Create a close task for the RC operators to confirm"
              }
              className="mr-auto"
            >
              {pendingClose ? "Close pending" : "Initiate close"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isMutating || !appIds.length || !name.trim()}
            onClick={submit}
          >
            {editing
              ? "Save"
              : appIds.length > 1
                ? `Create ${appIds.length} RCs`
                : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
